import { useMutation, useQueryClient } from '@tanstack/react-query';

import { SavedFilters } from '@air/api';
import { type SavedFilter, type SavedFiltersListResponse, type UpdateSavedFilterInput } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { isPrivateSavedSearchKey } from '~/components/v2/hooks/queries/usePrivateSavedSearch';
import { isPrivateSavedSearchListKey } from '~/components/v2/hooks/queries/usePrivateSavedSearchList';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { createEmptyListResponseStructure } from '~/utils/ApiUtils';
import { getSavedFilterKey } from './useGetSavedFilter';
import { getSavedFiltersKey } from './useGetSavedFilters';

export const useUpdateSavedFilter = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();

  const updateSavedFilter = useMutation({
    mutationFn: (updatedSavedFilter: UpdateSavedFilterInput) => {
      const workspaceId = currentWorkspace?.id;
      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      return SavedFilters.updateSavedFilter({ workspaceId, update: updatedSavedFilter });
    },
    // When mutate is called:
    onMutate: async (updatedSavedFilter) => {
      if (!currentWorkspace?.id) {
        return;
      }

      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: getSavedFiltersKey(currentWorkspace.id) });

      // Snapshot the previous value
      const previousSavedFilters = queryClient.getQueryData<SavedFiltersListResponse>(
        getSavedFiltersKey(currentWorkspace.id),
      );

      // if it exists (which it should), update the individual saved filter in cache
      queryClient.setQueryData<SavedFilter | undefined>(getSavedFilterKey(updatedSavedFilter.id), (cache) =>
        cache
          ? {
              ...cache,
              ...updatedSavedFilter,
            }
          : undefined,
      );

      // if it exists (which it should), update the individual saved filter in the list
      queryClient.setQueryData<SavedFiltersListResponse>(getSavedFiltersKey(currentWorkspace.id), (cache) =>
        cache
          ? {
              ...cache,
              data: cache.data.map((sv) =>
                sv.id === updatedSavedFilter.id
                  ? {
                      ...sv,
                      ...updatedSavedFilter,
                    }
                  : sv,
              ),
            }
          : createEmptyListResponseStructure(),
      );

      // ------------------------------------------------------------
      // @TODO - Remove everything above this post UI2
      // ------------------------------------------------------------

      queryClient.cancelQueries({ predicate: (query) => isPrivateSavedSearchListKey(query.queryKey) });

      const previousPrivateSavedSearchList = queryClient.getQueriesData<SavedFiltersListResponse>({
        predicate: (query) => isPrivateSavedSearchListKey(query.queryKey),
      });

      previousPrivateSavedSearchList.forEach((query) => {
        queryClient.setQueryData<SavedFiltersListResponse>(query[0], (cache) =>
          cache
            ? {
                ...cache,
                data: cache.data.map((sv) => (sv.id === updatedSavedFilter.id ? { ...sv, ...updatedSavedFilter } : sv)),
              }
            : createEmptyListResponseStructure(),
        );
      });

      queryClient.cancelQueries({ predicate: (query) => isPrivateSavedSearchKey(query.queryKey) });

      const previousPrivateSavedSearch = queryClient.getQueriesData<SavedFilter>({
        predicate: (query) => isPrivateSavedSearchKey(query.queryKey) && query.queryKey[1].id === updatedSavedFilter.id,
      });

      previousPrivateSavedSearch.forEach((query) => {
        queryClient.setQueryData<SavedFilter | undefined>(query[0], (cache) =>
          cache ? { ...cache, ...updatedSavedFilter } : undefined,
        );
      });

      // Return a context object with the snapshotted value
      return {
        previousSavedFilters,
        previousPrivateSavedSearchList,
        previousPrivateSavedSearch,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ predicate: (query) => isPrivateSavedSearchKey(query.queryKey) });
      queryClient.invalidateQueries({ predicate: (query) => isPrivateSavedSearchListKey(query.queryKey) });
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (error, updatedSavedFilter, context) => {
      if (!currentWorkspace?.id) {
        return;
      }

      queryClient.setQueryData(getSavedFiltersKey(currentWorkspace.id), context?.previousSavedFilters);

      reportErrorToBugsnag({
        error,
        context: 'Unable to update saved filter',
        metadata: {
          key: 'data',
          data: { updatedSavedFilter, workspaceId: currentWorkspace?.id },
        },
      });

      context?.previousPrivateSavedSearchList.forEach((query) => {
        queryClient.setQueryData<SavedFiltersListResponse>(...query);
      });

      context?.previousPrivateSavedSearch.forEach((query) => {
        queryClient.setQueryData<SavedFilter | undefined>(...query);
      });
    },
  });

  return {
    updateSavedFilter,
  };
};
